import CourseCard, { type Course } from '@/components/CourseCard';
import PageTitle from '@/components/PageTitle';
import ThemeWrapper from '@/components/ThemeWrapper';
import useApi from '@/hooks/useApi';
import useInertiaData from '@/hooks/useInertiaData';
import { useThemeColors } from '@/hooks/useThemeColors';
import AppLayout from '@/layouts/AppLayout';
import { Link, router, usePage } from '@inertiajs/react';
import { GraduationCap, Info, Layers } from 'lucide-react';
import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import ContinueLearning from '../Home/Partials/ContinueLearning';
import OnboardingSection, { OnboardingData } from '../Home/Partials/Onboarding/OnboardingSection';

interface GroupItem {
    item_type: 'group';
    id: number;
    title: string;
    image?: string | null;
    order: number;
    total_topics: number;
    total_classes: number;
    completed_classes: number;
    progress_percentage: number;
}

type CategoryItem = (Course & { item_type: 'course' }) | GroupItem;

interface Category {
    id: number;
    name: string;
    master?: boolean;
    items: CategoryItem[];
}

interface Props {
    categories_with_courses?: Category[];
    onboarding?: OnboardingData | null;
    academy_cover_photo_url?: string | null;
}

const GroupCard: React.FC<{ item: GroupItem }> = ({ item }) => {
    const { t } = useTranslation();
    const colors = useThemeColors();
    const { account_paused } = useInertiaData();
    const totalTopics = item.total_topics ?? 0;
    const totalClasses = item.total_classes ?? 0;
    const progressPercentage = item.progress_percentage ?? 0;

    const cardContent = (
        <>
            <div className="relative h-48 overflow-hidden bg-gray-200">
                {item.image ? (
                    <img
                        src={item.image}
                        alt={item.title}
                        className="h-full w-full object-cover transition-transform duration-300 group-hover:scale-110"
                    />
                ) : (
                    <div className="flex h-full items-center justify-center bg-gradient-to-br from-indigo-500 to-purple-600">
                        <Layers className="h-16 w-16 text-white opacity-50" />
                    </div>
                )}
                <div className="bg-opacity-60 absolute top-2 left-2 rounded bg-black px-2 py-1 text-xs text-white">
                    {totalTopics > 0 ? (
                        <span>{totalTopics} {t('home.topics')}</span>
                    ) : (
                        <span>{totalClasses} {t('course.classes_other')}</span>
                    )}
                </div>
            </div>
            <div className="p-4">
                <h3 className="mb-2 line-clamp-2 text-lg font-semibold text-gray-900">
                    {item.title}
                </h3>
                <div className="mb-3">
                    <div className="mb-1 flex items-center justify-between">
                        <span className="text-sm font-medium text-gray-700">
                            {progressPercentage}{t('home.percentCompleted')}
                        </span>
                    </div>
                    <div className="h-1.5 w-full overflow-hidden rounded-full bg-gray-200">
                        <div
                            className="h-full rounded-full transition-all duration-300"
                            style={{ width: `${progressPercentage}%`, backgroundColor: colors.progressBar }}
                        />
                    </div>
                </div>
            </div>
        </>
    );

    if (account_paused) {
        return (
            <div className="group relative cursor-not-allowed overflow-hidden rounded-lg bg-white shadow-md grayscale opacity-60">
                {cardContent}
            </div>
        );
    }

    return (
        <Link
            href={`/courses/groups/${item.id}/`}
            className="group relative cursor-pointer overflow-hidden rounded-lg bg-white shadow-md transition-all duration-300 hover:shadow-xl"
        >
            {cardContent}
        </Link>
    );
};

const IndexContent: React.FC = () => {
    const { t } = useTranslation();
    const { props } = usePage<Props>();
    const { categories_with_courses = [], onboarding, academy_cover_photo_url } =
        props;
    const colors = useThemeColors();
    const api = useApi();
    const { user } = useInertiaData();

    const [showOnboarding, setShowOnboarding] = useState(
        onboarding?.is_visible ?? false,
    );

    const handleDismiss = () => {
        setShowOnboarding(false);
        api.post('/onboarding/hide/');
    };

    const handleReset = () => {
        setShowOnboarding(true);
        api.post('/onboarding/reset/');
    };

    const handleCourseClick = (course: Course) => {
        router.visit(`/courses/${course.slug}/`);
    };

    const academyName = user?.current_academy?.name || 'la academia';
    const coverPhotoAlt = `Foto de portada de ${academyName}`;

    return (
        <AppLayout>
            <PageTitle title={t('home.courses')} />
            <div className="mx-auto max-w-6xl space-y-6">
                {/* Onboarding Section */}
                {showOnboarding && onboarding ? (
                    <OnboardingSection onboarding={onboarding} onDismiss={handleDismiss} />
                ) : null}

                {/* Watch again notice */}
                {!showOnboarding && onboarding ? (
                    <div className={`flex items-center gap-2 rounded-lg border ${colors.border} ${colors.headerBg} px-4 py-3 text-sm ${colors.accentText}`}>
                        <Info size={16} className="shrink-0" />
                        <span>
                            {t('onboarding.watchAgainNotice')}{' '}
                            <button
                                onClick={handleReset}
                                className={`font-semibold underline underline-offset-2 ${colors.accentText}`}
                            >
                                {t('onboarding.watchAgainAction')}
                            </button>
                        </span>
                    </div>
                ) : null}

                {academy_cover_photo_url ? (
                    <div className="rounded-lg border border-gray-200 bg-white p-3 shadow-sm">
                        <img
                            src={academy_cover_photo_url}
                            alt={coverPhotoAlt}
                            className="w-full rounded-lg"
                        />
                    </div>
                ) : null}

                {/* Continue where you left off */}
                <ContinueLearning />

                {categories_with_courses.length === 0 ? (
                    <div className="rounded-lg border border-gray-200 bg-white p-8 shadow-sm">
                        <div className="py-12 text-center">
                            <GraduationCap className="mx-auto mb-4 h-12 w-12 text-gray-400" />
                            <h3 className="mb-2 text-xl font-semibold text-gray-900">
                                {t('home.noCoursesEnrolled')}
                            </h3>
                            <p className="text-gray-600">
                                {t('home.exploreCatalog')}
                            </p>
                        </div>
                    </div>
                ) : (
                    <>
                        {categories_with_courses.map((category) => (
                            <div key={category.id} className="space-y-4">
                                <div className="flex items-baseline gap-3">
                                    <h3 className="text-xl font-semibold text-gray-900">
                                        {category.name}
                                    </h3>
                                    <span className="text-sm text-gray-500">
                                        {category.items.length}{' '}{t('myAccount.coursesCount', { count: category.items.length })}
                                    </span>
                                </div>
                                <div className="grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-3">
                                    {category.items.map((item) => {
                                        if (item.item_type === 'group') {
                                            return (
                                                <GroupCard
                                                    key={`group_${item.id}`}
                                                    item={item}
                                                />
                                            );
                                        }
                                        return (
                                            <CourseCard
                                                key={`course_${item.id}`}
                                                course={item}
                                                onClick={() =>
                                                    handleCourseClick(item)
                                                }
                                            />
                                        );
                                    })}
                                </div>
                            </div>
                        ))}
                    </>
                )}
            </div>
        </AppLayout>
    );
};

const Index: React.FC<Props> = () => {
    return (
        <ThemeWrapper>
            <IndexContent />
        </ThemeWrapper>
    );
};

export default Index;
